"use client";

import React from "react";
import { usePathname } from "next/navigation";
import { ContentCard } from "fansunited-sports-ui";
import { LinkPagination } from "@/components/ui/LinkPagination";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { AdSlot } from "@/features/ads/AdSlot";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { Post } from "@/payload-types";
import { getMediaFieldUrl } from "@/utilities/media/getMediaUrl";
import type { LocaleCode } from "@/lib/labels/types";

interface ArticlesLayoutDefaultProps {
    posts: Post[];
    currentPage: number;
    totalPages: number;
    locale: LocaleCode;
    headerColors?: ResolvedHeaderColors;
    breadcrumbs?: React.ReactNode;
}

export function ArticlesLayoutDefault({
    posts,
    currentPage,
    totalPages,
    locale,
    headerColors,
    breadcrumbs,
}: ArticlesLayoutDefaultProps) {
    const labels = useLabels();
    const pathname = usePathname();

    const getPageHref = (page: number) => {
        const params = new URLSearchParams();
        if (page > 1) {
            params.set("page", String(page));
        }
        return params.toString() ? `${pathname}?${params.toString()}` : pathname;
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const getImageUrl = (heroImage: Post["heroImage"]): string | undefined => {
        return getMediaFieldUrl(heroImage, "medium") ?? undefined;
    };

    return (
        <div className="min-h-screen">
            <BrandedHeader
                title={labels.nav.articles}
                subtitle=""
                colors={headerColors?.colors}
                backgroundImageUrl={headerColors?.backgroundImageUrl}
                titleAs="h1"
            />

            {breadcrumbs}

            <div className="max-w-350 mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map((post) => (
                        <ContentCard
                            key={post.id}
                            content={{
                                id: String(post.id),
                                type: "article",
                                link: `/articles/${post.slug}`,
                                title: post.title,
                                description: post.summary || "",
                                image: {
                                    url: getImageUrl(post.heroImage) || "",
                                    alt: post.title,
                                },
                                metadata: {
                                    time: formatDate(post.publishedAt),
                                },
                            }}
                            variant="standard"
                            styles={{
                                typography: {
                                    title: "text-lg font-semibold",
                                    description: "text-sm text-muted-foreground line-clamp-3",
                                },
                            }}
                        />
                    ))}
                </div>

                {posts.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">No articles found.</div>
                )}

                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                        <LinkPagination currentPage={currentPage} totalPages={totalPages} getPageHref={getPageHref} />
                    </div>
                )}

                <AdSlot id="content-bottom" />
            </div>
        </div>
    );
}
